'use client';

import { useTranslation } from 'react-i18next';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';

const SUBPROCESSORS = [
  {
    name: 'Hetzner Online GmbH',
    purpose: 'Hosting, database, object storage',
    location: 'Germany',
  },
  {
    name: 'Cloudflare, Inc.',
    purpose: 'Reverse proxy, CDN, DDoS protection, Turnstile bot protection',
    location: 'United States (EU edge nodes)',
  },
  {
    name: 'Auth0 (Okta, Inc.)',
    purpose: 'Authentication and identity management',
    location: 'EU tenant; US support access',
  },
  {
    name: 'Stripe Payments Europe Limited',
    purpose: 'Payment processing, invoicing, tax',
    location: 'Ireland; group sub-processors in the United States',
  },
  {
    name: 'Microsoft Ireland Operations Limited',
    purpose: 'Transactional email (Azure Communication Services)',
    location: 'European Union; Microsoft group sub-processors in the United States',
  },
  {
    name: 'Google Ireland Limited',
    purpose: 'Website analytics (only with user consent)',
    location: 'Ireland; United States',
  },
  {
    name: 'Anthropic, PBC',
    purpose: 'Large language model API for AI insights',
    location: 'United States',
  },
  {
    name: 'OpenAI Ireland Limited',
    purpose: 'Large language model API for AI insights',
    location: 'Ireland; United States',
  },
];

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <h3 className="text-base font-semibold text-foreground mb-2">{title}</h3>
      <div className="space-y-2 text-sm text-muted-foreground">{children}</div>
    </div>
  );
}

function BulletList({ items }: { items: string[] }) {
  return (
    <ul className="list-disc pl-5 space-y-1">
      {items.map((item, i) => (
        <li key={i}>{item}</li>
      ))}
    </ul>
  );
}

export default function DpaPage() {
  const { t } = useTranslation();
  const p = (key: string) => t(`pages.dpa.${key}`);

  return (
    <div className="grid grid-cols-1 gap-5 lg:gap-7.5 mt-5 mx-auto w-full max-w-4xl px-4 pb-12">
      <Card>
        <CardHeader className="flex-col items-start py-5">
          <CardTitle as="h1" className="text-xl">
            {p('title')}
          </CardTitle>
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mt-1">
            {p('effective')}
          </p>
          <p className="text-sm text-muted-foreground mt-2">{p('subtitle')}</p>
        </CardHeader>
        <CardContent className="space-y-8">
          {/* Preamble */}
          <Section title={p('preamble.title')}>
            <p>{p('preamble.p1')}</p>
            <p>{p('preamble.p2')}</p>
            <p>{p('preamble.p3')}</p>
          </Section>

          {/* 1. Definitions */}
          <Section title={p('s1.title')}>
            <p>{p('s1.body')}</p>
          </Section>

          {/* 2. Subject Matter and Duration */}
          <Section title={p('s2.title')}>
            <p>{p('s2.subject')}</p>
            <p>{p('s2.duration')}</p>
          </Section>

          {/* 3. Nature, Purpose, Categories */}
          <Section title={p('s3.title')}>
            <p>{p('s3.body')}</p>
          </Section>

          {/* 4. Controller's Obligations */}
          <Section title={p('s4.title')}>
            <p>{p('s4.intro')}</p>
            <BulletList
              items={[
                p('s4.lawful'),
                p('s4.instructions'),
                p('s4.informs'),
              ]}
            />
          </Section>

          {/* 5. Processor's Obligations */}
          <Section title={p('s5.title')}>
            <p>{p('s5.intro')}</p>
            <BulletList
              items={[
                p('s5.instructions'),
                p('s5.lawful'),
                p('s5.personnel'),
                p('s5.security'),
                p('s5.subprocessor'),
                p('s5.assistDataSubject'),
                p('s5.assistController'),
                p('s5.breach'),
                p('s5.deletion'),
                p('s5.audit'),
              ]}
            />
          </Section>

          {/* 6. Sub-processors */}
          <Section title={p('s6.title')}>
            <p>{p('s6.general')}</p>
            <p>{p('s6.notify')}</p>
            <p>{p('s6.object')}</p>
            <p>{p('s6.contract')}</p>
            <p>{p('s6.liability')}</p>
          </Section>

          {/* 7. International Transfers */}
          <Section title={p('s7.title')}>
            <p>{p('s7.body')}</p>
          </Section>

          {/* 8. Records of Processing */}
          <Section title={p('s8.title')}>
            <p>{p('s8.body')}</p>
          </Section>

          {/* 9. Audits and Inspections */}
          <Section title={p('s9.title')}>
            <p>{p('s9.intro')}</p>
            <p>{p('s9.info')}</p>
            <p>{p('s9.onSite')}</p>
          </Section>

          {/* 10. Liability */}
          <Section title={p('s10.title')}>
            <p>{p('s10.body')}</p>
          </Section>

          {/* 11. Term and Termination */}
          <Section title={p('s11.title')}>
            <p>{p('s11.term')}</p>
            <p>{p('s11.termination')}</p>
            <p>{p('s11.returnDelete')}</p>
          </Section>

          {/* 12. Governing Law */}
          <Section title={p('s12.title')}>
            <p>{p('s12.body')}</p>
          </Section>

          {/* 13. Final Provisions */}
          <Section title={p('s13.title')}>
            <p>{p('s13.precedence')}</p>
            <p>{p('s13.severability')}</p>
            <p>{p('s13.form')}</p>
          </Section>

          {/* Annex 1 — Details of Processing */}
          <Section title={p('annex1.title')}>
            <p>{p('annex1.subjectMatter')}</p>
            <p>{p('annex1.natureIntro')}</p>
            <BulletList
              items={[
                p('annex1.natureQueries'),
                p('annex1.natureWebhooks'),
                p('annex1.natureContent'),
              ]}
            />
            <p>{p('annex1.carveOut')}</p>
            <p>{p('annex1.subjectsIntro')}</p>
            <BulletList
              items={[
                p('annex1.subjectsControllerStaff'),
                p('annex1.subjectsControllerEndUsers'),
              ]}
            />
            <p>{p('annex1.categoriesIntro')}</p>
            <BulletList
              items={[
                p('annex1.categoryIdentifiers'),
                p('annex1.categoryQueryLogs'),
                p('annex1.categoryContent'),
              ]}
            />
            <p>{p('annex1.duration')}</p>
          </Section>

          {/* Annex 2 — TOMs */}
          <Section title={p('annex2.title')}>
            <p>{p('annex2.intro')}</p>

            <p className="font-medium text-foreground">
              {p('annex2.confTitle')}
            </p>
            <BulletList
              items={[
                p('annex2.confAccess'),
                p('annex2.confRbac'),
                p('annex2.confPhysical'),
                p('annex2.confTransit'),
                p('annex2.confRest'),
              ]}
            />

            <p className="font-medium text-foreground">
              {p('annex2.intTitle')}
            </p>
            <BulletList
              items={[p('annex2.intInput'), p('annex2.intTransfer')]}
            />

            <p className="font-medium text-foreground">
              {p('annex2.availTitle')}
            </p>
            <BulletList
              items={[
                p('annex2.availBackup'),
                p('annex2.availDr'),
                p('annex2.availMonitoring'),
              ]}
            />

            <p className="font-medium text-foreground">
              {p('annex2.testTitle')}
            </p>
            <BulletList
              items={[
                p('annex2.testReview'),
                p('annex2.testIncident'),
                p('annex2.testVuln'),
              ]}
            />

            <p className="font-medium text-foreground">
              {p('annex2.minTitle')}
            </p>
            <BulletList items={[p('annex2.minMin'), p('annex2.minRet')]} />
          </Section>

          {/* Annex 3 — Sub-processors */}
          <Section title={p('annex3.title')}>
            <p>{p('annex3.intro')}</p>
            <div className="overflow-x-auto rounded-xl border border-border">
              <table className="w-full text-left text-sm">
                <thead className="bg-muted/60 text-foreground">
                  <tr>
                    <th className="px-4 py-3 font-medium">
                      {p('annex3.tableName')}
                    </th>
                    <th className="px-4 py-3 font-medium">
                      {p('annex3.tablePurpose')}
                    </th>
                    <th className="px-4 py-3 font-medium">
                      {p('annex3.tableLocation')}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {SUBPROCESSORS.map((sp) => (
                    <tr key={sp.name}>
                      <td className="px-4 py-3 font-medium text-foreground">
                        {sp.name}
                      </td>
                      <td className="px-4 py-3">{sp.purpose}</td>
                      <td className="px-4 py-3 text-muted-foreground">
                        {sp.location}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p>{p('annex3.noteMcp')}</p>
          </Section>
        </CardContent>
      </Card>
    </div>
  );
}
